import React, { useState, useEffect } from "react";
import { ChevronDown, Menu, X, CreditCard, Cloud, Shield, Handshake, BookOpen, Smartphone, Building, Users, Code, BarChart, Heart, ArrowRight, Briefcase } from "lucide-react";
import { Link } from 'react-router-dom';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [dropdownStates, setDropdownStates] = useState({
    products: false,
    services: false,
    about: false,
  });
  const [isScrolled, setIsScrolled] = useState(false);

  // Handle scroll effect for header shadow
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleDropdown = (dropdown) => {
    setDropdownStates((prev) => ({
      products: false,
      services: false,
      about: false,
      [dropdown]: !prev[dropdown],
    }));
  };

  const closeAllDropdowns = () => {
    setDropdownStates({
      products: false,
      services: false,
      about: false,
    });
  };

  const handleMobileMenuToggle = () => {
    setIsMenuOpen(!isMenuOpen);
    closeAllDropdowns();
  };

  // Enhanced product items with new structure
  const productItems = {
    fintech: [
      // {
      //   category: 'PG With BlockChain',
      //   icon: <CreditCard className="w-5 h-5" />,
      //   items: [
      //     {
      //       name: 'Payin Solutions',
      //       href: '/PaymentSolutionsLanding',
      //       description: 'Seamless payment acceptance solutions'
      //     },
      //     {
      //       name: 'Payout Solutions',
      //       href: '/PayoutProcessLanding',
      //       description: 'Efficient bulk payout processing'
      //     }
      //   ]
      // },

      // Single items without subcategories

      {
        category: 'UPI Switch',
        icon: <Smartphone className="w-5 h-5" />,
        href: '/switch',
        description: 'Real-time UPI transaction processing and switching'
      },
      {
        category: 'Payment Gateway Suite',
        icon: <CreditCard className="w-5 h-5" />,
        href: '/paymentSolutions',
        description: 'A unified platform for collections & disbursements with advanced features'
      },
      {
        category: 'Recon with AI',
        icon: <BarChart className="w-5 h-5" />,
        href: '/Recon',
        description: 'Automated transaction reconciliation with AI-powered matching'
      },
      {
        category: 'Merchant Management',
        icon: <Handshake className="w-5 h-5" />,
        href: '/Merchant',
        description: 'Complete merchant lifecycle and onboarding management'
      },
      {
        category: 'NETC Solutions',
        icon: <Shield className="w-5 h-5" />,
        href: '/NETC',
        description: 'A unified platform for collections & disbursements with advanced features'
      },
      // {
      //   category: 'NETC FASTAG',
      //   icon: <Shield className="w-5 h-5" />,
      //   items: [
      //     {
      //       name: 'NETC Issuer',
      //       href: '/IssuerDashboard',
      //       description: 'FASTag issuance platform'
      //     },
      //     {
      //       name: 'NETC Acquirer',
      //       href: '/AcquirerServicesPage',
      //       description: 'Toll payment acquisition services'
      //     }
      //   ]
      // },
    ],
    nonFintech: [
      {
        name: 'Video Technology',
        href: '/HubLiveLanding',
        icon: <Smartphone className="w-5 h-5" />,
        description: 'Enterprise video solutions'
      },
      {
        name: 'Document Management System',
        href: '/dms',
        icon: <Code className="w-5 h-5" />,
        description: 'Digital document workflows'
      },
    ]
  };

  // Enhanced service items with icons and descriptions
  const serviceItems = {
    nonFintech: [
      {
        name: 'Engagement Models',
        href: '/staff-augmentation',
        icon: <Users className="w-5 h-5" />,
        description: 'Flexible team augmentation'
      },
      {
        name: 'Cyber Security',
        href: '/cybersecurity',
        icon: <Shield className="w-5 h-5" />,
        description: 'Comprehensive security solutions'
      },
      // {
      //   name: 'HealthCare Services',
      //   href: '/payxlanding',
      //   icon: <Heart className="w-5 h-5" />,
      //   description: 'Healthcare technology solutions'
      // },
      {
        name: 'Application Development',
        href: '/app-services',
        icon: <Code className="w-5 h-5" />,
        description: 'Custom software development'
      },
      {
        name: 'Cloud Offerings',
        href: '/cloud',
        icon: <Cloud className="w-5 h-5" />,
        description: 'End-to-end cloud solutions for secure, scalable, and cost-effective deployments'
      },
    ]
  };

  return (
    <header
      className={`bg-white sticky top-0 z-50 transition-all duration-500 ease-out ${isScrolled ? 'shadow-lg shadow-blue-500/10 border-b border-blue-100/30' : 'shadow-sm shadow-gray-100/30'
        }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center py-4">
          {/* Logo */}
          <div className="flex items-center transform transition-all duration-300 ease-out hover:scale-105">
            <a href="/" className="flex items-center">
              <img
                src="./logo.png"
                alt="AnviTech Solutions"
                className="w-64 h-auto filter transition-all duration-300 ease-out hover:brightness-110"
              />
            </a>
          </div>

          {/* Desktop Navigation - Centered */}
          <nav className="hidden lg:flex space-x-8 items-center justify-center flex-1">
            {/* Products Dropdown */}
            <div
              className="relative group"
              onMouseEnter={() => toggleDropdown("products")}
              onMouseLeave={closeAllDropdowns}
            >
              <button
                className="flex items-center space-x-1 text-gray-700 hover:text-blue-600 py-2 transition-all duration-300 ease-out relative after:absolute after:bottom-0 after:left-0 after:w-0 after:h-0.5 after:bg-gradient-to-r after:from-blue-600 after:to-blue-700 after:transition-all after:duration-300 after:ease-out hover:after:w-full group"
                onClick={() => toggleDropdown("products")}
              >
                <span className="font-semibold tracking-wide text-sm">OUR SOLUTIONS</span>
                <ChevronDown className={`w-4 h-4 transition-all duration-300 ease-out ${dropdownStates.products ? 'rotate-180 text-blue-600' : 'group-hover:rotate-12'
                  }`} />
              </button>

              <div className="absolute top-full left-0 w-full h-3" />

              <div
                className={`absolute top-full left-1/2 transform -translate-x-1/2 mt-3 w-[700px] bg-white rounded-2xl shadow-xl border border-gray-100 py-8 transition-all duration-500 ease-out origin-top max-h-[80vh] overflow-y-auto ${dropdownStates.products
                  ? 'opacity-100 scale-100 translate-y-0'
                  : 'opacity-0 scale-95 -translate-y-4 pointer-events-none'
                  }`}
              >
                <div className="px-8">
                  <div className="grid grid-cols-2 gap-8">
                    {/* For Businesses - Fintech Section with Categories */}
                    <div className="space-y-2">
                      <div className="pb-3 border-b border-blue-100">
                        <h3 className="text-base font-bold text-gray-900 mb-1 bg-gradient-to-r from-blue-600 to-blue-700 bg-clip-text text-transparent">
                          For Businesses
                        </h3>
                        <p className="text-xs text-gray-500">Fintech & Payment Solutions</p>
                      </div>

                      {productItems.fintech.map((category, categoryIndex) => (
                        <div key={category.category} className="space-y-2">
                          {/* Check if it has subcategories (items) or is a direct link */}
                          {category.items && category.items.length > 0 ? (
                            <>
                              {/* Category with subcategories */}
                              <div className="flex items-center space-x-2 py-1">
                                <div className="text-blue-600">
                                  {category.icon}
                                </div>
                                <h4 className="text-sm font-bold text-blue-700 uppercase tracking-wide">
                                  {category.category}
                                </h4>
                              </div>

                              <div className="space-y-1 pl-7">
                                {category.items.map((item, itemIndex) => (
                                  <a
                                    key={item.name}
                                    href={item.href}
                                    className="flex items-start space-x-3 p-2 text-gray-700 hover:text-blue-600 hover:bg-blue-50/70 rounded-lg transition-all duration-300 ease-out transform hover:translate-x-1 hover:shadow-sm group"
                                  >
                                    <div className="text-blue-400 group-hover:text-blue-600 transition-colors duration-300 mt-0.5">
                                      <ArrowRight className="w-3 h-3" />
                                    </div>
                                    <div className="flex-1">
                                      <div className="font-medium text-xs group-hover:text-blue-600 transition-colors duration-300">
                                        {item.name}
                                      </div>
                                      <div className="text-xs text-gray-500 mt-0.5 group-hover:text-gray-600 transition-colors duration-300">
                                        {item.description}
                                      </div>
                                    </div>
                                  </a>
                                ))}
                              </div>
                            </>
                          ) : (
                            /* Direct category link */
                            <a
                              href={category.href}
                              className="flex items-start space-x-3 p-2 text-gray-700 hover:text-blue-600 hover:bg-blue-50/70 rounded-lg transition-all duration-300 ease-out transform hover:translate-x-1 hover:shadow-sm group"
                            >
                              <div className="text-blue-600 group-hover:text-blue-700 transition-colors duration-300 mt-0.5">
                                {category.icon}
                              </div>
                              <div className="flex-1">
                                <div className="font-medium text-sm group-hover:text-blue-600 transition-colors duration-300">
                                  {category.category}
                                </div>
                                {category.description && (
                                  <div className="text-xs text-gray-500 mt-0.5 group-hover:text-gray-600 transition-colors duration-300">
                                    {category.description}
                                  </div>
                                )}
                              </div>
                            </a>
                          )}
                        </div>
                      ))}
                    </div>

                    {/* Technology Solutions Section */}
                    <div className="space-y-4">
                      <div className="pb-3 border-b border-green-100">
                        <h3 className="text-base font-bold text-gray-900 mb-1 bg-gradient-to-r from-green-600 to-green-700 bg-clip-text text-transparent">
                          Technology Solutions
                        </h3>
                        <p className="text-xs text-gray-500">Enterprise & Digital Platforms</p>
                      </div>
                      <div className="space-y-1">
                        {productItems.nonFintech.map((item, index) => (
                          <a
                            key={item.name}
                            href={item.href}
                            className="flex items-start space-x-3 p-2 text-gray-700 hover:text-green-600 hover:bg-green-50/70 rounded-lg transition-all duration-300 ease-out transform hover:translate-x-1 hover:shadow-sm group"
                          >
                            <div className="text-green-500 group-hover:text-green-600 transition-colors duration-300 mt-0.5">
                              {item.icon}
                            </div>
                            <div className="flex-1">
                              <div className="font-medium text-xs group-hover:text-green-600 transition-colors duration-300">
                                {item.name}
                              </div>
                              <div className="text-xs text-gray-500 mt-0.5 group-hover:text-gray-600 transition-colors duration-300">
                                {item.description}
                              </div>
                            </div>
                          </a>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Services Dropdown */}
            <div
              className="relative group"
              onMouseEnter={() => toggleDropdown("services")}
              onMouseLeave={closeAllDropdowns}
            >
              <button
                className="flex items-center space-x-1 text-gray-700 hover:text-blue-600 py-2 transition-all duration-300 ease-out relative after:absolute after:bottom-0 after:left-0 after:w-0 after:h-0.5 after:bg-gradient-to-r after:from-blue-600 after:to-blue-700 after:transition-all after:duration-300 after:ease-out hover:after:w-full group"
                onClick={() => toggleDropdown("services")}
              >
                <span className="font-semibold tracking-wide text-sm">SERVICES</span>
                <ChevronDown className={`w-4 h-4 transition-all duration-300 ease-out ${dropdownStates.services ? 'rotate-180 text-blue-600' : 'group-hover:rotate-12'
                  }`} />
              </button>

              <div className="absolute top-full left-0 w-full h-3" />

              <div
                className={`absolute top-full left-1/2 transform -translate-x-1/2 mt-3 w-80 bg-white rounded-2xl shadow-xl border border-gray-100 py-6 transition-all duration-500 ease-out origin-top max-h-[70vh] overflow-y-auto ${dropdownStates.services
                  ? 'opacity-100 scale-100 translate-y-0'
                  : 'opacity-0 scale-95 -translate-y-4 pointer-events-none'
                  }`}
              >
                <div className="px-6">
                  <div className="pb-3 border-b border-purple-100 mb-4">
                    <h3 className="text-base font-bold text-gray-900 mb-1 bg-gradient-to-r from-purple-600 to-purple-700 bg-clip-text text-transparent">
                      Professional Services
                    </h3>
                    <p className="text-xs text-gray-500">Technology & Consulting Services</p>
                  </div>
                  <div className="space-y-1">
                    {serviceItems.nonFintech.map((item, index) => (
                      <a
                        key={item.name}
                        href={item.href}
                        className="flex items-start space-x-3 p-2 text-gray-700 hover:text-purple-600 hover:bg-purple-50/70 rounded-lg transition-all duration-300 ease-out transform hover:translate-x-1 hover:shadow-sm group"
                      >
                        <div className="text-purple-500 group-hover:text-purple-600 transition-colors duration-300 mt-0.5">
                          {item.icon}
                        </div>
                        <div className="flex-1">
                          <div className="font-medium text-xs group-hover:text-purple-600 transition-colors duration-300">
                            {item.name}
                          </div>
                          <div className="text-xs text-gray-500 mt-0.5 group-hover:text-gray-600 transition-colors duration-300">
                            {item.description}
                          </div>
                        </div>
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <a
              href="/industry"
              className="text-gray-700 hover:text-blue-600 py-2 transition-all duration-300 ease-out relative after:absolute after:bottom-0 after:left-0 after:w-0 after:h-0.5 after:bg-gradient-to-r after:from-blue-600 after:to-blue-700 after:transition-all after:duration-300 after:ease-out hover:after:w-full font-semibold tracking-wide transform hover:scale-105 text-sm"
            >
              INDUSTRIES WE SERVE
            </a>

            {/* About Dropdown */}
            <div
              className="relative group"
              onMouseEnter={() => toggleDropdown("about")}
              onMouseLeave={closeAllDropdowns}
            >
              <button
                className="flex items-center space-x-1 text-gray-700 hover:text-blue-600 py-2 transition-all duration-300 ease-out relative after:absolute after:bottom-0 after:left-0 after:w-0 after:h-0.5 after:bg-gradient-to-r after:from-blue-600 after:to-blue-700 after:transition-all after:duration-300 after:ease-out hover:after:w-full group"
                onClick={() => toggleDropdown("about")}
              >
                <span className="font-semibold tracking-wide text-sm">ABOUT</span>
                <ChevronDown className={`w-4 h-4 transition-all duration-300 ease-out ${dropdownStates.about ? 'rotate-180 text-blue-600' : 'group-hover:rotate-12'
                  }`} />
              </button>

              <div className="absolute top-full left-0 w-full h-3" />

              <div
                className={`absolute top-full left-1/2 transform -translate-x-1/2 mt-3 w-48 bg-white rounded-2xl shadow-xl border border-gray-100 py-4 transition-all duration-500 ease-out origin-top max-h-[60vh] overflow-y-auto ${dropdownStates.about
                  ? 'opacity-100 scale-100 translate-y-0'
                  : 'opacity-0 scale-95 -translate-y-4 pointer-events-none'
                  }`}
              >
                <div className="px-4 space-y-1">
                  {[
                    { name: 'About Us', href: '/Aboutus', icon: <Building className="w-4 h-4" /> },
                    { name: 'Careers', href: '/careers', icon: <Users className="w-4 h-4" /> },
                    { name: 'Contact', href: '/Contact', icon: <Smartphone className="w-4 h-4" /> },
                    { name: 'Clients', href: '/clients', icon: <Briefcase className="w-4 h-4" /> },

                  ].map((item, index) => (
                    <a
                      key={item.name}
                      href={item.href}
                      className="flex items-center space-x-3 px-3 py-2 text-sm text-gray-700 hover:bg-blue-50/70 hover:text-blue-600 rounded-lg transition-all duration-300 ease-out transform hover:translate-x-1 hover:shadow-sm group"
                    >
                      <div className="text-blue-500 group-hover:text-blue-600 transition-colors duration-300">
                        {item.icon}
                      </div>
                      <span className="font-medium">{item.name}</span>
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </nav>

          {/* Get In Touch Button */}
          <div className="hidden lg:flex items-center">
            <Link to="/contact">
              <button className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-8 py-3 rounded-full text-sm font-semibold hover:from-blue-700 hover:to-blue-800 transition-all duration-300 ease-out transform hover:scale-105 hover:shadow-lg hover:shadow-blue-500/25 relative overflow-hidden group">
                <span className="relative z-10">Get In Touch</span>
                <div className="absolute inset-0 bg-gradient-to-r from-blue-700 to-blue-800 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </button>
            </Link>
          </div>

          {/* Mobile menu button */}
          <button
            className="lg:hidden p-2 rounded-xl hover:bg-gray-100/80 transition-all duration-300 ease-out hover:shadow-sm transform hover:scale-105"
            onClick={handleMobileMenuToggle}
          >
            <div className="relative w-6 h-6">
              <Menu className={`w-6 h-6 absolute transition-all duration-500 ease-out ${isMenuOpen ? 'opacity-0 rotate-180 scale-50' : 'opacity-100 rotate-0 scale-100'
                }`} />
              <X className={`w-6 h-6 absolute transition-all duration-500 ease-out ${isMenuOpen ? 'opacity-100 rotate-0 scale-100' : 'opacity-0 -rotate-180 scale-50'
                }`} />
            </div>
          </button>
        </div>
      </div>

      {/* Mobile Navigation */}
      <div className={`lg:hidden bg-white border-t border-gray-100 overflow-hidden transition-all duration-700 ease-out ${isMenuOpen ? 'max-h-[100vh] opacity-100 shadow-xl' : 'max-h-0 opacity-0'
        }`}>
        <div className="px-4 py-6 space-y-4 max-h-[70vh] overflow-y-auto">
          {/* Mobile Products */}
          <div className="border-b border-gray-100/50 pb-4">
            <button
              className="w-full flex items-center justify-between py-3 text-gray-700 font-semibold hover:text-blue-600 transition-all duration-300 ease-out rounded-lg hover:bg-blue-50/50"
              onClick={() => toggleDropdown("products")}
            >
              <span className="tracking-wide text-sm">OUR SOLUTIONS</span>
              <ChevronDown
                className={`w-4 h-4 transform transition-all duration-500 ease-out ${dropdownStates.products ? "rotate-180 text-blue-600" : ""
                  }`}
              />
            </button>
            <div className={`overflow-hidden transition-all duration-700 ease-out ${dropdownStates.products ? 'max-h-[800px] opacity-100' : 'max-h-0 opacity-0'
              }`}>
              <div className="pl-4 pt-4 space-y-6">
                {/* Mobile Fintech Products with Categories */}
                <div>
                  <h4 className="text-sm font-bold text-blue-600 mb-4 uppercase tracking-wide bg-gradient-to-r from-blue-600 to-blue-700 bg-clip-text text-transparent">
                    For Businesses
                  </h4>
                  <div className="space-y-4">
                    {productItems.fintech.map((category, categoryIndex) => (
                      <div key={category.category} className="space-y-2">
                        {/* Check if it has subcategories (items) or is a direct link for mobile */}
                        {category.items && category.items.length > 0 ? (
                          <>
                            {/* Mobile Category Header */}
                            <div className="flex items-center space-x-2 py-1 px-2 bg-blue-50/30 rounded-lg">
                              <div className="text-blue-600">
                                {category.icon}
                              </div>
                              <h5 className="text-xs font-bold text-blue-700 uppercase tracking-wide">
                                {category.category}
                              </h5>
                            </div>

                            {/* Mobile Category Items */}
                            <div className="space-y-1 pl-6">
                              {category.items.map((item, itemIndex) => (
                                <a
                                  key={item.name}
                                  href={item.href}
                                  className="flex items-center space-x-3 py-2 px-3 text-gray-600 hover:text-blue-600 hover:bg-blue-50/70 rounded-lg transition-all duration-300 ease-out transform hover:translate-x-1 hover:shadow-sm group"
                                >
                                  <div className="text-blue-400 group-hover:text-blue-600 transition-colors duration-300">
                                    <ArrowRight className="w-3 h-3" />
                                  </div>
                                  <div className="flex-1">
                                    <div className="text-sm font-medium">{item.name}</div>
                                    <div className="text-xs text-gray-500 mt-0.5">{item.description}</div>
                                  </div>
                                </a>
                              ))}
                            </div>
                          </>
                        ) : (
                          /* Direct category link for mobile */
                          <a
                            href={category.href}
                            className="flex items-center space-x-3 py-2 px-3 text-gray-600 hover:text-blue-600 hover:bg-blue-50/70 rounded-lg transition-all duration-300 ease-out transform hover:translate-x-1 hover:shadow-sm group"
                          >
                            <div className="text-blue-600 group-hover:text-blue-700 transition-colors duration-300">
                              {category.icon}
                            </div>
                            <div className="flex-1">
                              <div className="text-sm font-medium">{category.category}</div>
                              {category.description && (
                                <div className="text-xs text-gray-500 mt-0.5">{category.description}</div>
                              )}
                            </div>
                          </a>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Mobile Non-Fintech Products */}
                <div>
                  <h4 className="text-sm font-bold text-green-600 mb-3 uppercase tracking-wide bg-gradient-to-r from-green-600 to-green-700 bg-clip-text text-transparent">
                    Technology Solutions
                  </h4>
                  <div className="space-y-1">
                    {productItems.nonFintech.map((item, index) => (
                      <a
                        key={item.name}
                        href={item.href}
                        className="flex items-center space-x-3 py-3 px-3 text-gray-600 hover:text-green-600 hover:bg-green-50/70 rounded-lg transition-all duration-300 ease-out transform hover:translate-x-1 hover:shadow-sm group"
                      >
                        <div className="text-green-500 group-hover:text-green-600 transition-colors duration-300">
                          {item.icon}
                        </div>
                        <div className="flex-1">
                          <div className="text-sm font-medium">{item.name}</div>
                          <div className="text-xs text-gray-500 mt-0.5">{item.description}</div>
                        </div>
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Mobile Services */}
          <div className="border-b border-gray-100/50 pb-4">
            <button
              className="w-full flex items-center justify-between py-3 text-gray-700 font-semibold hover:text-blue-600 transition-all duration-300 ease-out rounded-lg hover:bg-blue-50/50"
              onClick={() => toggleDropdown("services")}
            >
              <span className="tracking-wide text-sm">SERVICES</span>
              <ChevronDown
                className={`w-4 h-4 transform transition-all duration-500 ease-out ${dropdownStates.services ? "rotate-180 text-blue-600" : ""
                  }`}
              />
            </button>
            <div className={`overflow-hidden transition-all duration-700 ease-out ${dropdownStates.services ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
              }`}>
              <div className="pl-4 pt-4 space-y-1">
                {serviceItems.nonFintech.map((item, index) => (
                  <a
                    key={item.name}
                    href={item.href}
                    className="flex items-center space-x-3 py-3 px-3 text-gray-600 hover:text-purple-600 hover:bg-purple-50/70 rounded-lg transition-all duration-300 ease-out transform hover:translate-x-1 hover:shadow-sm group"
                  >
                    <div className="text-purple-500 group-hover:text-purple-600 transition-colors duration-300">
                      {item.icon}
                    </div>
                    <div className="flex-1">
                      <div className="text-sm font-medium">{item.name}</div>
                      <div className="text-xs text-gray-500 mt-0.5">{item.description}</div>
                    </div>
                  </a>
                ))}
              </div>
            </div>
          </div>

          <div className="border-b border-gray-100/50 pb-4">
            <a
              href="/industry"
              className="block py-3 text-gray-700 font-semibold hover:text-blue-600 transition-all duration-300 ease-out rounded-lg hover:bg-blue-50/50 tracking-wide text-sm"
            >
              INDUSTRIES WE SERVE
            </a>
          </div>

          {/* Mobile About */}
          <div className="border-b border-gray-100/50 pb-4">
            <button
              className="w-full flex items-center justify-between py-3 text-gray-700 font-semibold hover:text-blue-600 transition-all duration-300 ease-out rounded-lg hover:bg-blue-50/50"
              onClick={() => toggleDropdown("about")}
            >
              <span className="tracking-wide text-sm">ABOUT</span>
              <ChevronDown
                className={`w-4 h-4 transform transition-all duration-500 ease-out ${dropdownStates.about ? "rotate-180 text-blue-600" : ""
                  }`}
              />
            </button>
            <div className={`overflow-hidden transition-all duration-700 ease-out ${dropdownStates.about ? 'max-h-64 opacity-100' : 'max-h-0 opacity-0'
              }`}>
              <div className="pl-4 pt-4 space-y-1">
                {[
                  { name: 'About Us', href: '/Aboutus', icon: <Building className="w-4 h-4" /> },
                  { name: 'Careers', href: '/careers', icon: <Users className="w-4 h-4" /> },
                  { name: 'Contact', href: '/Contact', icon: <Smartphone className="w-4 h-4" /> },
                  { name: 'Clients', href: '/clients', icon: <Briefcase className="w-4 h-4" /> },

                ].map((item, index) => (
                  <a
                    key={item.name}
                    href={item.href}
                    className="flex items-center space-x-3 py-3 px-3 text-gray-600 hover:text-blue-600 hover:bg-blue-50/70 rounded-lg transition-all duration-300 ease-out transform hover:translate-x-1 hover:shadow-sm group"
                  >
                    <div className="text-blue-500 group-hover:text-blue-600 transition-colors duration-300">
                      {item.icon}
                    </div>
                    <span className="font-medium text-sm">{item.name}</span>
                  </a>
                ))}
              </div>
            </div>
          </div>

          {/* Mobile Get In Touch Button */}
          <div className="pt-4">
            <a
              href="./contact-us"
              target="_blank"
              rel="noopener noreferrer"
              className="block w-full"
            >
              <button className="w-full bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-4 rounded-xl text-sm font-semibold hover:from-blue-700 hover:to-blue-800 transition-all duration-300 ease-out transform hover:scale-105 hover:shadow-lg hover:shadow-blue-500/25 relative overflow-hidden group">
                <span className="relative z-10">Get In Touch</span>
                <div className="absolute inset-0 bg-gradient-to-r from-blue-700 to-blue-800 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </button>
            </a>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;